'use client'

import React, { useState } from 'react'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon, SearchIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import ContentPagination from '@/components/partials/content-pagination'
import AddressCard from './address-card'
import AddressDialog from './address-dialog'
import { listAddresses } from '@/lib/api'
import { AddressesListResponse } from '@/types/address'

const AddressesPageContent = () => {
  const t = useTranslations()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [createOpen, setCreateOpen] = useState(false)
  const [inputValue, setInputValue] = useState(searchParams.get('q') ?? '')

  const search = searchParams.get('q') ?? ''
  const page = Number(searchParams.get('page') ?? '1')

  const { data, isLoading, isError, refetch } = useQuery<AddressesListResponse>({
    queryKey: ['addresses', search, page],
    queryFn: () => listAddresses({ q: search || undefined, page, size: 12 })
  })

  const addresses = data?.items ?? []
  const totalPages = data?.pages ?? 1

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const q = inputValue.trim()
    const params = new URLSearchParams(searchParams.toString())
    if (q) {
      params.set('q', q)
    } else {
      params.delete('q')
    }
    params.delete('page')
    router.push(`${pathname}?${params.toString()}`)
  }

  const handleClear = () => {
    setInputValue('')
    const params = new URLSearchParams(searchParams.toString())
    params.delete('q')
    params.delete('page')
    router.push(`${pathname}?${params.toString()}`)
  }

  return (
    <div className="flex w-full flex-col gap-6 rounded-lg bg-white p-6 shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <form onSubmit={handleSearch} className="w-full sm:w-80">
          <InputGroup>
            <InputGroupAddon>
              <SearchIcon className="h-4 w-4" />
            </InputGroupAddon>
            <InputGroupInput
              name="q"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder={t('search')}
            />
            <InputGroupAddon align="inline-end">
              {inputValue && (
                <InputGroupButton type="button" variant="ghost" onClick={handleClear} tabIndex={-1}>
                  <XIcon className="h-3.5 w-3.5" />
                </InputGroupButton>
              )}
              <InputGroupButton type="submit" variant="ghost">
                <SearchIcon className="h-3.5 w-3.5" />
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
        </form>
        <Button onClick={() => setCreateOpen(true)} className="shrink-0">
          <PlusIcon className="mr-1 h-4 w-4" />
          {t('add_address')}
        </Button>
      </div>

      <Separator />

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-lg" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className="flex flex-col items-center gap-3 py-10">
          <p className="text-destructive text-sm">{t('something_went_wrong')}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('try_again')}
          </Button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && addresses.length === 0 && (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-muted-foreground text-sm">{t('no_addresses')}</p>
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon className="mr-1 h-4 w-4" />
            {t('add_address')}
          </Button>
        </div>
      )}

      {/* Address cards */}
      {!isLoading && !isError && addresses.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {addresses.map(address => (
            <AddressCard key={address.id} address={address} />
          ))}
        </div>
      )}

      {/* Pagination */}
      <ContentPagination currentPage={page} totalPages={totalPages} />

      {/* Create dialog */}
      <AddressDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  )
}

export default AddressesPageContent
